import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, MenuItem, Alert, Table, TableHead, TableBody,
  TableRow, TableCell, CircularProgress, Chip, Paper, Grid, LinearProgress, Tooltip,
} from '@mui/material';
import { TrendingUp, Person, FactCheck } from '@mui/icons-material';
import axios from 'axios';
import { LineChart } from './LernAlytics';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Per-subject performance for one class: a grade breakdown (driven by the
// configurable GradingScale, so it fits both 8-4-4 letter grades and CBC levels)
// plus a performance-over-time trend across terms — mirroring the legacy
// analytics screen. Only "core" subject classes (a specific stream) can be
// analysed; pooled electives have no single class.
export default function SubjectAnalysis({ subjectClasses = [], year, term }) {
  const options = useMemo(
    () => subjectClasses.filter(s => s.kind === 'core' && s.classCode && s.areaCode),
    [subjectClasses]
  );
  const [key, setKey] = useState('');
  const [category, setCategory] = useState('');   // '' = All, else Formative/Summative
  const [strand, setStrand] = useState('');        // '' = All strands
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!key && options.length) setKey(`${options[0].classCode}|${options[0].areaCode}`);
  }, [options, key]);

  // A new subject class resets the filters (strands are per subject).
  useEffect(() => { setCategory(''); setStrand(''); }, [key]);

  const load = useCallback(async () => {
    if (!key) return;
    const [classCode, areaCode] = key.split('|');
    setLoading(true); setError(''); setData(null);
    try {
      const [{ data: analysis }, { data: prog }] = await Promise.all([
        axios.get(`${API}/api/academics/class-teacher/subject-analysis`, {
          headers: hdrs(), params: { classCode, areaCode, academicYear: year, term, category, strand } }),
        axios.get(`${API}/api/academics/class-teacher/curriculum-progress`, {
          headers: hdrs(), params: { classCode, areaCode, academicYear: year, term } }),
      ]);
      setData(analysis);
      setProgress(prog);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subject analysis');
    } finally { setLoading(false); }
  }, [key, year, term, category, strand]);

  useEffect(() => { load(); }, [load]);

  const chartPoints = useMemo(
    () => (data?.series || []).map(s => ({ label: s.label, value: s.meanMarks })),
    [data]
  );

  const gradeColorMap = useMemo(
    () => Object.fromEntries((data?.grades || []).map(g => [g.code, g.color])),
    [data]
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField select label="Subject Class" value={key} size="small"
          onChange={e => setKey(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 300 } }}>
          {options.length === 0 && <MenuItem value="" disabled>No subject classes assigned</MenuItem>}
          {options.map(o => (
            <MenuItem key={`${o.classCode}|${o.areaCode}`} value={`${o.classCode}|${o.areaCode}`}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? <CircularProgress /> : !data ? (
        options.length === 0
          ? <Typography color="text.secondary">You have no per-stream subject classes to analyse.</Typography>
          : null
      ) : (
        <Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
            <Typography variant="h6">{data.className} — {data.areaName}</Typography>
            {data.subjectTeacher && (
              <Chip size="small" icon={<Person />} label={data.subjectTeacher} />
            )}
            <Chip size="small" variant="outlined" label={`${data.academicYear} · Term ${data.term}`} />
          </Box>

          {/* Curriculum Progress (CBC coverage of the planned assessment) */}
          {progress && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                <FactCheck sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                Curriculum Progress
              </Typography>
              {!progress.hasSetup ? (
                <Typography variant="body2" color="text.secondary">
                  No assessment plan set up for this subject in {progress.academicYear} Term {progress.term}.
                </Typography>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Formative Assessments', b: progress.formative, color: '#1565c0' },
                      { label: 'Projects', b: progress.projects, color: '#6a1b9a' },
                      { label: 'Summative Exams', b: progress.summative, color: '#00838f' },
                    ].map(({ label, b, color }) => (
                      <Grid item xs={12} sm={4} key={label}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {b.analyzed} of {b.total} analyzed · {b.pct}%
                        </Typography>
                        <LinearProgress variant="determinate" value={b.pct}
                          sx={{ height: 8, borderRadius: 4, mt: 0.5,
                            '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                      </Grid>
                    ))}
                  </Grid>
                  {progress.strands.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">By strand</Typography>
                      {progress.strands.map(s => (
                        <Box key={s.strand} display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ minWidth: 140 }}>{s.strand}</Typography>
                          <LinearProgress variant="determinate" value={s.pct}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} />
                          <Typography variant="caption" sx={{ minWidth: 78, textAlign: 'right' }}>
                            {s.analyzed}/{s.total} · {s.pct}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    A component counts as analyzed once all {progress.rosterCount} learner(s) have a mark.
                  </Typography>
                </>
              )}
            </Paper>
          )}

          {/* Grade breakdown for the selected period */}
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Grade breakdown</Typography>
            <Box flexGrow={1} />
            <TextField select size="small" label="Category" value={category}
              onChange={e => setCategory(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">All assessments</MenuItem>
              <MenuItem value="Formative">Formative</MenuItem>
              <MenuItem value="Summative">Summative</MenuItem>
            </TextField>
            <TextField select size="small" label="Strand" value={strand}
              onChange={e => setStrand(e.target.value)} sx={{ minWidth: 160 }}
              disabled={!data.availableStrands?.length}>
              <MenuItem value="">All strands</MenuItem>
              {(data.availableStrands || []).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>
          {(category || strand) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Breakdown recomputed from {category || 'all'}{strand ? ` · ${strand}` : ''} components only. The trend below stays the overall subject mean.
            </Typography>
          )}
          {data.breakdown.entries === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No marks entered for this subject in {data.academicYear} Term {data.term} yet.
            </Alert>
          ) : (
            <Box sx={{ overflowX: 'auto', mb: 3 }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    {data.grades.map(g => <TableCell key={g.code} align="center"><strong>{g.code}</strong></TableCell>)}
                    <Tooltip title="Not assessed"><TableCell align="center"><strong>X</strong></TableCell></Tooltip>
                    <Tooltip title="Incomplete assessment"><TableCell align="center"><strong>Y</strong></TableCell></Tooltip>
                    <TableCell align="center"><strong>Entries</strong></TableCell>
                    <TableCell align="center"><strong>Mean Marks</strong></TableCell>
                    <TableCell align="center"><strong>MM Dev</strong></TableCell>
                    {data.hasPoints && <TableCell align="center"><strong>Mean Points</strong></TableCell>}
                    {data.hasPoints && <TableCell align="center"><strong>MP Dev</strong></TableCell>}
                    <TableCell align="center"><strong>Grade</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {data.grades.map(g => (
                      <TableCell key={g.code} align="center"
                        sx={{ color: data.breakdown.counts[g.code] ? gradeColorMap[g.code] : 'text.disabled',
                              fontWeight: data.breakdown.counts[g.code] ? 700 : 400 }}>
                        {data.breakdown.counts[g.code] || 0}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ color: data.breakdown.x ? '#d32f2f' : 'text.disabled' }}>{data.breakdown.x || 0}</TableCell>
                    <TableCell align="center" sx={{ color: data.breakdown.y ? '#ed6c02' : 'text.disabled' }}>{data.breakdown.y || 0}</TableCell>
                    <TableCell align="center">{data.breakdown.entries}</TableCell>
                    <TableCell align="center"><strong>{data.breakdown.meanMarks}</strong></TableCell>
                    <TableCell align="center">{data.breakdown.mmDev}</TableCell>
                    {data.hasPoints && <TableCell align="center">{data.breakdown.meanPoints}</TableCell>}
                    {data.hasPoints && <TableCell align="center">{data.breakdown.mpDev}</TableCell>}
                    <TableCell align="center">
                      <Chip size="small" label={data.breakdown.grade || '—'}
                        sx={{ bgcolor: gradeColorMap[data.breakdown.grade] || '#9e9e9e', color: '#fff', fontWeight: 700 }} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Performance over time */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
            Performance over time — mean marks (%)
          </Typography>
          {data.series.length < 2 ? (
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
              Not enough history yet — at least two exam periods are needed for a trend.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
              <LineChart points={chartPoints} />
            </Paper>
          )}

          {data.series.length > 0 && (
            <Grid container>
              <Grid item xs={12}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Period</strong></TableCell>
                        <TableCell align="center"><strong>Entries</strong></TableCell>
                        <TableCell align="center"><strong>Mean Marks</strong></TableCell>
                        {data.hasPoints && <TableCell align="center"><strong>Mean Points</strong></TableCell>}
                        <TableCell align="center"><strong>Grade</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.series.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>{s.label}</TableCell>
                          <TableCell align="center">{s.entries}</TableCell>
                          <TableCell align="center">{s.meanMarks}</TableCell>
                          {data.hasPoints && <TableCell align="center">{s.meanPoints}</TableCell>}
                          <TableCell align="center">
                            <Chip size="small" label={s.grade || '—'}
                              sx={{ bgcolor: gradeColorMap[s.grade] || '#9e9e9e', color: '#fff', fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
}
